import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { 
  Filter, RefreshCw, Search, ChevronLeft, ChevronRight,
  Truck, PackageOpen, User, FileText, Upload, Trash2,
  Edit, CheckCircle, RotateCcw, MapPin, Warehouse, 
  Clock, Send, Image, Key, Building, AlertTriangle,
  ShoppingCart, ArrowRight, Users
} from 'lucide-react';
import { getLogs } from '../api';

const Activitylog = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Activity Logs');
  const navigate = useNavigate();
  
  // State for logs data
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // State for filters
  const [usernameFilter, setUsernameFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [detailsFilter, setDetailsFilter] = useState("");
  const [availableCategories, setAvailableCategories] = useState([]);

  // Define action categories with styles and icons
  const actionCategories = {
    // Transport Management
    transport: {
      color: "bg-blue-100 text-blue-800",
      icon: <Truck className="h-4 w-4 mr-1" />,
      actions: ["create_transport_line", "update_transport_line", "update_transport_line_status", "reject_transport_line"],
      label: "Transporte"
    },
    // Vehicle Management
    vehicle: {
      color: "bg-green-100 text-green-800",
      icon: <Building className="h-4 w-4 mr-1" />,
      actions: ["create_vehicle", "update_vehicle", "delete_vehicle", "create_remolque", "update_remolque"],
      label: "Vehículo"
    },
    // Driver Management
    driver: {
      color: "bg-yellow-100 text-yellow-800",
      icon: <User className="h-4 w-4 mr-1" />,
      actions: ["create_operator", "update_operator"],
      label: "Operador"
    },
    // Document Management
    document: {
      color: "bg-purple-100 text-purple-800",
      icon: <FileText className="h-4 w-4 mr-1" />,
      actions: ["upload_document", "delete_document"],
      label: "Documento"
    },
    // Order Management
    order: {
      color: "bg-orange-100 text-orange-800",
      icon: <PackageOpen className="h-4 w-4 mr-1" />,
      actions: ["create_order", "update_order", "delete_order", "revert_order", "change_order_status", "update_order_stop"],
      label: "Orden"
    },
    // Warehouse Management
    warehouse: {
      color: "bg-cyan-100 text-cyan-800",
      icon: <Warehouse className="h-4 w-4 mr-1" />,
      actions: ["update_warehouse_status", "update_dev_route_dt", "update_devolucion_dt"],
      label: "Almacén"
    },
    // Shipment Management
    shipment: {
      color: "bg-indigo-100 text-indigo-800",
      icon: <ShoppingCart className="h-4 w-4 mr-1" />,
      actions: ["Create Shipment", "Delete Shipment", "Update Shipment Status", "Update Shipment Loading Time", 
                "Update Shipment Status Deliver", "update_transport_details", "Update Orders Status"],
      label: "Embarque"
    },
    // Notification
    notification: {
      color: "bg-pink-100 text-pink-800",
      icon: <Send className="h-4 w-4 mr-1" />,
      actions: ["Notify Transporter SMS", "Correo Transportista"],
      label: "Notificación"
    },
    // Evidence
    evidence: {
      color: "bg-red-100 text-red-800",
      icon: <Image className="h-4 w-4 mr-1" />,
      actions: ["Upload Photos", "Delete All Photos", "Upload Evidence", "Delete Evidence", "Update POD Time"],
      label: "Evidencia"
    },
    // User Management
    user: {
      color: "bg-violet-100 text-violet-800",
      icon: <Users className="h-4 w-4 mr-1" />,
      actions: ["update_user_role", "login"],
      label: "Usuario"
    },
    // Other
    other: {
      color: "bg-gray-100 text-gray-800", 
      icon: <AlertTriangle className="h-4 w-4 mr-1" />,
      actions: [],
      label: "Otros"
    }
  };

  // Updated action mappings with technical name to Spanish display name
  const actionMappings = {
    // Transport Management
    "create_transport_line": "Creacion de Linea de transporte",
    "update_transport_line": "Actualizacion de Linea de transporte",
    "update_transport_line_status": "Cambio de estado de Linea de transporte",
    "reject_transport_line": "Rechazo de Linea de transporte",
    
    // Vehicle Management
    "create_vehicle": "Creacion de Vehiculo",
    "update_vehicle": "Actualizacion de Vehiculo",
    "delete_vehicle": "Borrado de Vehiculo",
    "create_remolque": "Creacion de Remolque",
    "update_remolque": "Actualizacion de Remolque",
    
    // Driver Management
    "create_operator": "Creacion de Conductor",
    "update_operator": "Actualizacion de Conductor",
    
    // Document Management
    "upload_document": "Carga de Documento",
    "delete_document": "Borrado de Documento",
    
    // Order Management
    "create_order": "Creacion de orden",
    "update_order": "Actualizacion de orden",
    "delete_order": "Borrado de orden",
    "revert_order": "Reversion de orden",
    "change_order_status": "Cambio de estado de orden",
    "update_order_stop": "Actualizacion de parada",
    
    // Warehouse Management
    "update_warehouse_status": "Actualizacion de Almacen devolucion",
    "update_dev_route_dt": "Actualizacion fecha de ruta devolucion",
    "update_devolucion_dt": "Actualizacion fecha de devolcion",
    
    // Shipment Management
    "Create Shipment": "Creacion de embarque",
    "Delete Shipment": "Borrado de embarque",
    "Update Shipment Status": "Actualizacion de estatus",
    "Update Shipment Loading Time": "Actualizar fecha de carga",
    "Update Shipment Status Deliver": "Confirmacion de entrega",
    "update_transport_details": "Actualizacion de detalles de transporte",
    "Update Orders Status": "Actualizacion de estatus de ordenes",
    
    // Notification
    "Notify Transporter SMS": "Mensaje enviado",
    "Correo Transportista": "Correo enviado",
    
    // Evidence
    "Upload Photos": "Fotos cargadas",
    "Delete All Photos": "Borrado de fotos",
    "Upload Evidence": "Evidencia cargada",
    "Delete Evidence": "Borrado de evidencia",
    "Update POD Time": "Actualizacion Hora de evidencia",
    
    // User Management
    "update_user_role": "Cambio de rol",
    "login": "Inicio de sesion",
  };

  // Reverse mapping from Spanish to technical names for filtering
  const reverseActionMappings = Object.entries(actionMappings).reduce(
    (acc, [key, value]) => {
      acc[value.toLowerCase()] = key;
      return acc;
    }, {}
  );

  // Helper function to get action category
  const getActionCategory = (action) => {
    for (const [category, data] of Object.entries(actionCategories)) {
      if (data.actions.some(actionType => action.toLowerCase().includes(actionType.toLowerCase()))) {
        return { category, ...data };
      }
    }
    return { category: 'other', ...actionCategories.other };
  };

  // New function to get human-readable action descriptions
  const getActionDescription = (action) => {
    // Return the mapping if it exists or format the original action name
    return actionMappings[action] || action.split("_").map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(" ");
  };

  // Function to format action description for better readability
  const formatActionDescription = (action, details) => {
    // If no details are provided, just return the action with proper formatting
    if (!details) {
      return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    // Extract meaningful information based on action type
    if (action.includes('transport_line')) {
      if (action.includes('create')) {
        return <>Created transport line <span className="font-medium">{details.split(': ')[1]?.split(' (')[0]}</span></>;
      } else if (action.includes('update_status')) {
        const parts = details.split(': ');
        const statusParts = parts[2]?.split(' → ');
        return <>Changed status of <span className="font-medium">{parts[1]?.split(',')[0]}</span> from <span className="line-through">{statusParts?.[0]}</span> to <span className="font-medium">{statusParts?.[1]}</span></>;
      }
    } 
    
    if (action.includes('order')) {
      if (action.includes('create')) {
        const orderInfo = details.split(' para cliente ');
        return <>Created order <span className="font-medium">{orderInfo[0].split(': ')[1]}</span> for client <span className="font-medium">{orderInfo[1]}</span></>;
      } else if (action.includes('delete')) {
        const orderInfo = details.match(/\#(\d+) \(DO: ([^,]+), Cliente: ([^\)]+)/);
        if (orderInfo) {
          return <>Deleted order <span className="font-medium">#{orderInfo[1]} ({orderInfo[2]})</span> for client <span className="font-medium">{orderInfo[3]}</span></>;
        }
      } else if (action.includes('status')) {
        const statusInfo = details.match(/(\#\S+) modificado a: ([^,]+), Subestatus: ([^,]+)/);
        if (statusInfo) {
          return <>Changed status of order <span className="font-medium">{statusInfo[1]}</span> to <span className="font-medium">{statusInfo[2]} - {statusInfo[3]}</span></>;
        }
      }
    }

    if (action.includes('Shipment')) {
      if (action.includes('Create')) {
        return <>Created shipment <span className="font-medium">{details.split(': ')[1]}</span></>;
      } else if (action.includes('Status')) {
        const shipmentInfo = details.match(/Shipment: ([^,]+), Status: ([^,]+), Substatus: ([^\s]+)/);
        if (shipmentInfo) {
          return <>Updated shipment <span className="font-medium">{shipmentInfo[1]}</span> to status <span className="font-medium">{shipmentInfo[2].split('.')[1]} - {shipmentInfo[3].split('.')[1]}</span></>;
        }
      }
    }

    if (action.includes('user_role')) {
      const roleInfo = details.match(/usuario '([^']+)' \(ID: (\d+)\): ([^ ]+) → ([^ ]+)/);
      if (roleInfo) {
        return <>Changed role of user <span className="font-medium">{roleInfo[1]}</span> from <span className="line-through">{roleInfo[3]}</span> to <span className="font-medium">{roleInfo[4]}</span></>;
      }
    }

    // Default fallback to the original details
    return details;
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      
      // Calculate skip based on current page and items per page
      const skip = (currentPage - 1) * itemsPerPage;
      
      // Build query params
      let params = {
        skip,
        limit: itemsPerPage,
      };
      
      if (usernameFilter) params.username = usernameFilter;
      
      // Handle action filter - match against Spanish descriptions
      if (actionFilter) {
        // Try to find an exact match in the Spanish translations
        const techActions = Object.keys(actionMappings).filter(techAction => 
          actionMappings[techAction].toLowerCase().includes(actionFilter.toLowerCase())
        );
        
        if (techActions.length > 0) {
          // If we found technical actions that match the Spanish filter,
          // use those for the backend filter (using OR logic)
          params.action = techActions.join(',');
        } else {
          // If no match in translations, fall back to the raw filter
          params.action = actionFilter;
        }
      }
      
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();
      
      // Add details filter to params
      if (detailsFilter) params.details = detailsFilter;
      
      // Use the getLogs function from api.js
      const data = await getLogs(params);
      
      // Here we properly handle the response structure
      if (data && data.logs) {
        let filteredLogs = [...data.logs];

        // Apply category filter client-side if selected
        if (categoryFilter) {
          filteredLogs = filteredLogs.filter(log => {
            const { category } = getActionCategory(log.action);
            return category === categoryFilter;
          });
          
          // Update total count for pagination when filtering by category
          setTotalLogs(filteredLogs.length);
        } else {
          setTotalLogs(data.total || 0);
        }
        
        setLogs(filteredLogs);
        
        // Calculate and set available categories based on current logs
        if (data.logs.length > 0) {
          // Get unique categories from the logs
          const categories = data.logs.map(log => getActionCategory(log.action).category);
          const uniqueCategories = [...new Set(categories)];
          
          // Map to category objects with labels
          const categoriesWithLabels = uniqueCategories.map(category => ({
            value: category,
            label: actionCategories[category]?.label || category.charAt(0).toUpperCase() + category.slice(1)
          }));
          
          // Sort by label alphabetically
          categoriesWithLabels.sort((a, b) => a.label.localeCompare(b.label));
          
          setAvailableCategories(categoriesWithLabels);
        } else {
          setAvailableCategories([]);
        }
      } else {
        setLogs([]);
        setTotalLogs(0);
        setAvailableCategories([]);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      alert("Error al cargar los registros. Intente de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch logs on initial load and when filters/pagination changes
  useEffect(() => {
    fetchLogs();
  }, [currentPage, itemsPerPage]);
  
  const handleFilterApply = () => {
    setCurrentPage(1); // Reset to first page
    fetchLogs();
  };
  
  const handleClearFilters = () => {
    setUsernameFilter("");
    setActionFilter("");
    setStartDate("");
    setEndDate("");
    setCategoryFilter(""); // Clear category filter
    setDetailsFilter(""); // Clear details filter
    setCurrentPage(1);
    fetchLogs();
  };
  
  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Enhanced getActionBadge function
  const getActionBadge = (action) => {
    const { category, color, icon, label } = getActionCategory(action);
    
    return (
      <div className="flex items-center">
        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${color}`}>
          {icon}
          {label || category.charAt(0).toUpperCase() + category.slice(1)}
        </span>
      </div>
    );
  };
  
  const totalPages = Math.ceil(totalLogs / itemsPerPage);

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />
      <div className={`transition-all flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        <Topbar showSearch={false} />
        
        <div className="mt-6 bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Registros de Actividad</h2>
              <div className="flex gap-2">
                <button 
                  className="px-4 py-2 border rounded-md flex items-center gap-1"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4" />
                  {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
                </button>
                <button 
                  className="px-4 py-2 border rounded-md flex items-center gap-1"
                  onClick={fetchLogs} 
                >
                  <RefreshCw className="h-4 w-4" />
                  Actualizar
                </button>
              </div>
            </div>
            
            {showFilters && (
              <div className="bg-gray-50 p-4 rounded-md mb-6 flex flex-wrap gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Usuario:</label>
                  <input
                    type="text"
                    placeholder="Filtrar por usuario"
                    value={usernameFilter}
                    onChange={(e) => setUsernameFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md w-56"
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Acción:</label>
                  <input
                    type="text"
                    placeholder="Filtrar por acción"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md w-56"
                  />
                </div>
                
                {/* Updated Category filter */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Categoría:</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md w-56"
                    disabled={availableCategories.length === 0}
                  >
                    <option value="">Todas las categorías</option>
                    {availableCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Details filter */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Detalles:</label>
                  <input
                    type="text"
                    placeholder="Buscar en detalles"
                    value={detailsFilter}
                    onChange={(e) => setDetailsFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md w-56"
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Fecha Inicial:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border rounded-md w-56"
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Fecha Final:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border rounded-md w-56"
                  />
                </div>
                
                <div className="flex items-end gap-2 ml-auto">
                  <button 
                    className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-1"
                    onClick={handleFilterApply}
                  >
                    <Search className="h-4 w-4" />
                    Aplicar Filtros
                  </button>
                  <button 
                    className="px-4 py-2 border rounded-md"
                    onClick={handleClearFilters}
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 rounded-full border-t-transparent"></div>
                <span className="ml-2">Cargando registros...</span>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-4 text-left font-medium text-gray-500 w-[50px]">ID</th>
                        <th className="p-4 text-left font-medium text-gray-500 w-[120px]">Usuario</th>
                        <th className="p-4 text-left font-medium text-gray-500 w-[120px]">Categoría</th>
                        <th className="p-4 text-left font-medium text-gray-500 w-[160px]">Tipo de Acción</th>
                        <th className="p-4 text-left font-medium text-gray-500 w-[160px]">Fecha y Hora</th>
                        <th className="p-4 text-left font-medium text-gray-500">Detalles</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {logs && logs.length > 0 ? (
                        logs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="p-4 font-medium">{log.id}</td>
                            <td className="p-4">{log.username}</td>
                            <td className="p-4">{getActionBadge(log.action)}</td>
                            <td className="p-4 text-gray-700 font-medium">
                              {getActionDescription(log.action)}
                            </td>
                            <td className="p-4 text-gray-500 text-sm">{formatTimestamp(log.timestamp)}</td>
                            <td className="p-4 max-w-[400px]">
                              {formatActionDescription(log.action, log.details) || "—"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center py-8">
                            No se encontraron registros.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    Mostrando {logs ? logs.length : 0} de {totalLogs} registros
                  </div>
                  <div className="flex items-center gap-2">
                    <select 
                      className="px-3 py-2 border rounded-md"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-500">por página</span>
                    
                    <div className="flex items-center gap-1 ml-4">
                      <button
                        className="p-1 border rounded-md"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm">
                        Página {currentPage} de {totalPages || 1}
                      </span>
                      <button
                        className="p-1 border rounded-md"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activitylog;
